import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import api, { getAuthToken } from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (pin: string, phone?: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already authenticated on app start
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          const userData = await api.auth.verifyToken();
          setUser(userData);
        } catch (error) {
          console.error('Token verification failed:', error);
          api.auth.logout();
        }
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (pin: string, phone?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { user: userData } = await api.auth.login(pin, phone);
      setUser(userData);
    } catch (error: any) {
      // In development mode, show detailed error information
      let errorMessage = error.response?.data?.message || 'Login failed';
      
      if (process.env.NODE_ENV === 'development') {
        // Add more detailed error info for developers
        const statusCode = error.response?.status;
        const errorDetails = error.response?.data?.error;
        const fullError = error.message;
        
        if (statusCode) {
          errorMessage += ` (${statusCode})`;
        }
        
        if (errorDetails && errorDetails !== errorMessage) {
          errorMessage += `\nDetails: ${errorDetails}`;
        }
        
        // If it's a network error, show that
        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
          errorMessage = `Network Error: Cannot connect to API server\nCode: ${error.code}\nMessage: ${fullError}`;
        }
        
        // Log full error for debugging
        console.error('Login Error Details:', {
          status: statusCode,
          message: error.response?.data?.message,
          error: errorDetails,
          fullError: error,
          code: error.code
        });
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    api.auth.logout();
    setUser(null);
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};